// Bloom filter over a Uint8Array bit vector. Double hashing (Kirsch &
// Mitzenmauer): two FNV-1a variants combined as h1 + i*h2, the standard trick
// for deriving k hash functions from two.

export class BloomFilter {
  readonly bits: Uint8Array;
  readonly numHashes: number;

  constructor(bits: Uint8Array, numHashes: number) {
    this.bits = bits;
    this.numHashes = numHashes;
  }

  static create(expectedKeys: number, bitsPerKey: number): BloomFilter {
    const nBits = Math.max(64, expectedKeys * bitsPerKey);
    const nBytes = Math.ceil(nBits / 8);
    // k = ln2 * bits/key is the optimal hash count.
    const k = Math.max(1, Math.min(12, Math.round(bitsPerKey * 0.69)));
    return new BloomFilter(new Uint8Array(nBytes), k);
  }

  add(key: string): void {
    const [h1, h2] = hashPair(key);
    const nBits = this.bits.length * 8;
    for (let i = 0; i < this.numHashes; i++) {
      const bit = (h1 + i * h2) % nBits;
      this.bits[bit >> 3] |= 1 << (bit & 7);
    }
  }

  mayContain(key: string): boolean {
    const [h1, h2] = hashPair(key);
    const nBits = this.bits.length * 8;
    for (let i = 0; i < this.numHashes; i++) {
      const bit = (h1 + i * h2) % nBits;
      if ((this.bits[bit >> 3] & (1 << (bit & 7))) === 0) return false;
    }
    return true;
  }
}

function hashPair(key: string): [number, number] {
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;
  for (let i = 0; i < key.length; i++) {
    const c = key.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 0x01000193) >>> 0;
    h2 = (Math.imul(h2 ^ (c + 7), 0x85ebca6b) ^ (h2 >>> 13)) >>> 0;
  }
  // Ensure h2 is odd so the double-hash probe sequence covers all bits.
  return [h1 >>> 0, (h2 | 1) >>> 0];
}
